import React, { useState, useEffect } from 'react';
import { reduce, isEmpty } from 'underscore';
import PropTypes from 'prop-types';
import { TreeView, CheckBoxNode } from '@bingads-webui-react/tree-view';
import { Button, Glyphicon } from '@bingads-webui-react/primitive';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getContextualOptionsAsync } from './contextual-repository';
import { getContextualTargetStyles } from './contextual-target-styles';
import { getSelectionFromTreeNodes, applySelectionToTreeNodes } from './contextual-options-helper';

const getClassNames = node => [`checkbox-node-${node.id}`];

const ContextualTarget = (props) => {
  const {
    i18n,
    classes,
    onContextualSelectionChange,
    contextualSelection,
    hideHeader,
    initialNodes,
    setInitialNodes,
  } = props;
  const [nodes, setNodes] = useState(initialNodes || []);

  const selectedNodes = nodes.filter(node => node.children.some(child => child.state && child.state.checked));

  const selectedNodesCount = reduce(
    selectedNodes,
    (memo, node) => memo + node.children.filter(child => child.state && child.state.checked).length,
    0
  );

  const onSelectionRemove = (node) => {
    // eslint-disable-next-line no-param-reassign
    node.state = { checked: false };

    if (initialNodes) {
      // if we get nodes from props, we should not modify them
      setInitialNodes([...initialNodes]);
    } else {
      setNodes([...nodes]);
    }
  };

  useEffect(() => {
    if (isEmpty(nodes)) {
      getContextualOptionsAsync().then((options) => {
        // apply initial selection state only once for initial load
        applySelectionToTreeNodes(options, contextualSelection);
        setNodes(options);
      });
    } else {
      applySelectionToTreeNodes(nodes, contextualSelection);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const selection = getSelectionFromTreeNodes(nodes);

    onContextualSelectionChange(selection);
  }, [nodes, onContextualSelectionChange]);

  const onChange = (changedNodes) => {
    if (initialNodes) {
      setInitialNodes(changedNodes);
    } else {
      setNodes(changedNodes);
    }
  };

  return (
    <div className={`${classes.root} contextual-target-container`}>
      {!hideHeader && (<h4 className={classes.contextualTargetTitle}>{ i18n.getString(_TL_('Contextual Targets')) }</h4>)}
      <div className={classes.targetsContainer}>
        <TreeView
          treeViewClassNames={`${classes.treeViewContainer} tree-view-container tree-view-checkbox-selection`}
          nodes={nodes}
          onChange={onChange}
          nodeRenderer={CheckBoxNode}
          getClassNames={getClassNames}
          autoHeight
          maxHeight={450}
        />
      </div>
      <div className={classes.selectionContainer}>
        <div>
          <span className="selectedTargetsLabel">
            { i18n.getString(_TL_('Selected Targets')) } : { selectedNodesCount }
          </span>
        </div>
        {
        selectedNodes.map(parentNode => (
          <div key={parentNode.id}>
            <div className="categoryName">{parentNode.name}</div>
            <div className="selectedNodesContainer">
              {
                parentNode.children.map((childNode) => {
                  if (childNode.state && childNode.state.checked) {
                    return (
                      <Button
                        className="selectedTargetButton"
                        onClick={() => onSelectionRemove(childNode)}
                        key={childNode.id}
                      >
                        {childNode.name.split(':', 2)[1]}
                        <Glyphicon glyph="ba-Cancel" />
                      </Button>
                    );
                  }
                  return null;
                })
              }
            </div>
          </div>
          ))
      }
      </div>
    </div>
  );
};

ContextualTarget.propTypes = {
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    treeViewContainer: PropTypes.string.isRequired,
    contextualTargetTitle: PropTypes.string.isRequired,
    targetsContainer: PropTypes.string.isRequired,
    selectionContainer: PropTypes.string.isRequired,
  }).isRequired,
  onContextualSelectionChange: PropTypes.func.isRequired,
  i18n: PropTypes.shape({ getString: PropTypes.func }).isRequired,
  contextualSelection: PropTypes.arrayOf(PropTypes.shape({
    CategoryId: PropTypes.number.isRequired,
    TargetOptionId: PropTypes.number.isRequired,
    CriterionId: PropTypes.number.isRequired,
  })).isRequired,
  hideHeader: PropTypes.bool,
  initialNodes: PropTypes.shape({}),
  setInitialNodes: PropTypes.func,
};

ContextualTarget.defaultProps = {
  hideHeader: false,
  initialNodes: null,
  setInitialNodes: () => {},
};

const ContextualTargetWithStyle = withDefaultStyles(ContextualTarget, getContextualTargetStyles);

export { ContextualTarget, ContextualTargetWithStyle };
